import React, { useCallback, useState } from 'react';
import { useThemeContext } from 'context/ThemeContext/ThemeContext';
import { SerialNumberRouterModuleData } from 'types/DigitalHubTypes';
import { showToast } from 'components/atomic/Toast/Toast';
import { useTranslation } from 'react-i18next';
import { isPreviewMode } from 'utils/preview';
import { useAPI } from 'utils/api';
import Input from 'components/atomic/Input';
import useRedirectLink from 'hooks/useRedirectLink';
import ProductButton from 'components/atomic/ProductButton';

type SerialRputerProps = {
  brandId: string;
  buttonStyle?: string;
  serialModule: SerialNumberRouterModuleData;
};

const SerialRouter: React.FC<SerialRputerProps> = ({
  brandId,
  buttonStyle,
  serialModule,
}) => {
  const [serialNumber, setSerialNumber] = useState<string>('');
  const [serialError, setSerialError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const redirectToExternalLink = useRedirectLink();
  const { brandTheme } = useThemeContext();
  const { t } = useTranslation('translation', {
    keyPrefix: 'digitalHub',
  });

  const endpoint = `digitalhub/${brandId}?serialNumber=${encodeURIComponent(
    serialNumber
  )}`;
  const [lookupSerialNumber] = useAPI({
    endpoint,
    method: 'GET',
  });

  const handleSerialNumberSubmit = async () => {
    if (!serialNumber) setSerialError(t('emptySerialNumberError'));
    else {
      setLoading(true);
      try {
        let redirectRoute = await lookupSerialNumber(undefined, null, true);
        if (redirectRoute) {
          showToast({
            message: t('serialNumberFound'),
            type: 'success',
          });
          setTimeout(() => {
            redirectToExternalLink(
              `${window.location.protocol}//${window.location.host}/c/${redirectRoute}`,
              '_blank',
              '',
              false
            );
          }, 1000);
        }
      } catch (error: any) {
        setSerialError(
          serialModule?.failureMessage
            ? serialModule?.failureMessage
            : t('serialNumberNotFoundError')
        );
      }
      setLoading(false);
    }
  };

  const getCornerRadius = () => {
    if (buttonStyle === 'largeCorner') return 'rounded-[38px]';
    else if (buttonStyle === 'smallCorner') return 'rounded-[10px]';
    else if (buttonStyle === 'noCorner') return 'rounded-none';
    else return 'rounded-[38px]';
  };

  const getPlaceholder = useCallback(() => {
    const inputLabel =
      serialModule?.inputLabel ?? t('serialNumberFilledInputPlaceholder');
    if (serialNumber?.length > 0) return inputLabel;
    return `${t('enterText')} ${inputLabel?.toLowerCase()}...`;
  }, [serialNumber, serialModule, t]);

  return (
    <section
      className={`relative w-full flex flex-col gap-3 bg-white shadow-md p-4 ${getCornerRadius()}`}
    >
      <Input
        value={serialNumber}
        placeholderStyle='!z-20'
        placeholder={getPlaceholder()}
        onChange={(event) => {
          setSerialNumber(event.target.value);
          setSerialError('');
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') handleSerialNumberSubmit();
        }}
      />
      {serialError && (
        <span className='px-4 text-warning text-xs -mt-2.5'>{serialError}</span>
      )}
      <ProductButton
        loading={loading}
        buttonStyle={buttonStyle}
        mediaLink={serialModule.image || ''}
        textColor={serialModule.color || 'white'}
        onClick={() => handleSerialNumberSubmit()}
        background={serialModule.backgroundColor || brandTheme}
        hideImage={serialModule.hideImage || !serialModule.image}
        title={serialModule.text || t('defaultSerialNumberButtonTitle')}
        variant={
          serialModule.hideImage || !serialModule.image ? 'compact' : 'default'
        }
      />
    </section>
  );
};

export default SerialRouter;
